<template>
    <Row type="none" class="bg-white rounded shadow">

        <b-modal :hide-header="true" footer-class="p-2 border-top-0 d-flex justify-content-between" id="delete">

            <template #default="{}">
                <h5 style="text-align: center">{{ $t('customer.hasDebts') }}</h5>
            </template>

            <template #modal-footer="{ ok, cancel }">
                <b-button variant="success" @click="cancel()">
                    {{ $t('cancelDelete') }}
                </b-button>
                <b-button variant="danger" @click="deleteOneCustomer(customerId); ok()">
                    {{ $t('customer.deleteAnyway') }}
                </b-button>
            </template>
        </b-modal>

        <div>
            <ButtonTag
                class="col-sm-5 col-md-3 mb-7"
                v-if="canManageCustomers()"
                url="customers/add"
            >
                {{ $t('customer.add') }}
            </ButtonTag>
            <div class="d-block d-sm-inline pt-1" style="position: absolute; right: 0; top: inherit">
                <span>
                    {{ $t('statistics.categories.debtor') }}:
                </span>
                <span class="text-primary">
                    {{ showCurrencyById(getUserCompany.baseCurrency.id, getCompanyRemaining.oweUs) }}
                </span>
            </div>
        </div>
        <SearchCollapse class="mt-3 mb-3 mb-sm-0" :do-action="showCheckboxes">
            <div class="p-0 d-sm-flex">
                <b-form-input
                    v-model="search.givenName"
                    :placeholder="$t('person.givenName')"
                    class="mr-md-1"
                    @keyup.enter="searchCustomers"
                >
                </b-form-input>
                <b-form-input
                    v-model="search.familyName"
                    :placeholder="$t('person.familyName')"
                    class="mr-md-1"
                    @keyup.enter="searchCustomers"
                >
                </b-form-input>
                <b-form-input
                    v-model="search.companyName"
                    :placeholder="$t('company.name')"
                    class="mr-md-1"
                    @keyup.enter="searchCustomers"
                >
                </b-form-input>
                <div class="d-flex justify-content-end">
                    <IconButton
                        title="search"
                        :do-action="searchCustomers"
                        icon="search"
                    />
                    <IconButton
                        title="filter.clear"
                        :do-action="resetCustomerSearchInfo"
                        icon="eraser"
                    />
                </div>
            </div>
        </SearchCollapse>

        <RowColumn>
            <TableTag
                getter-name="getCustomers"
                fetch-getter-name="fetchCustomersTotalDebt"
                :headers="headers"
                :body-properties="bodyProperties"
                :isSelectable=true
            >
                <template v-slot:custom="row">
                    <router-link :to="$i18nHelper.getUrl('customers/' + row.item.id)" style="color: #069F43">
                        {{
                            row.item.isCompany ? row.item.customerCompany.name : row.item.person.givenName + ' ' + row.item.person.familyName
                        }}
                    </router-link>
                </template>

                <template v-slot:telephone="row">
                    <span v-if="row.item.isCompany">
                        <span v-if="row.item.customerCompany.telephone !== null">{{
                                row.item.customerCompany.telephone
                            }}</span>
                    </span>
                    <span v-else>
                        <span v-if="row.item.person.telephone !== null">{{ row.item.person.telephone }}</span>
                    </span>
                </template>

                <template v-slot:actions="row">
                    <Modal
                        text="deleteConfirm"
                        ok-text="yes"
                        cancel-text="no"
                        :is-ok-only="false"
                        :context="{name: row.item.isCompany ? row.item.customerCompany.name : row.item.person.givenName + ' ' + row.item.person.familyName}"
                        :id="'delete-customer-' + row.item.id"
                        :on-ok-pressed="() => {deleteOneCustomer(row.item.id)}"
                    />
                </template>
                <template v-slot:checkboxes="row" v-if="canSeeCustomerDebts()">
                    <b-form-checkbox
                        class="mr-n4 mt-n1"
                        v-if="isShowCheckbox"
                        size="lg"
                        @change="getId(row.item)"
                        :value=row.item.id
                        v-model="selectedCustomers"
                    />
                </template>
                <template v-slot:checkAll_0 v-if="canSeeCustomerDebts()">
                    <b-form-checkbox
                        v-if="isShowCheckbox"
                        size="lg"
                        @change="selectAll"
                        v-model="isSelectedAll"
                    />
                </template>
            </TableTag>
            <div>
                <b-button
                    variant="danger"
                    size="sm"
                    v-if="selectedCustomers.length > 0"
                    style="background-color: red; color: #fff"
                    class="d-flex float-right mx-2"
                    @click="showModal">
                    {{ $t('delete') }}
                </b-button>
            </div>
            <div class="clear">
            </div>
            <b-modal id="delete-customer-modal" hide-footer>
                <!-- Modal body -->
                <div class="modal-body">
                    {{ $t('customer.deleteCustomWarning') }}
                </div>
                <!-- Modal footer -->
                <div class="modal-footer">
                    <button type="button" class="btn danger" data-dismiss="modal" @click="deleteCustomers">
                        {{ $t('yes') }}
                    </button>
                    <button type="button" class="btn btn-primary" data-dismiss="modal" @click="cancelDelete">
                        {{ $t('no') }}
                    </button>
                </div>
            </b-modal>
        </RowColumn>
        <PaginationRow
            :search="search"
            base-url="customer"
            method-name="searchCustomer"
            :total-items="getCustomersTotalCount"
        />

    </Row>
</template>

<script>
import Row from "../../components/html/Row";
import RowColumn from "../../components/RowColumn";
import {mapActions, mapGetters} from "vuex";
import ButtonTag from "../../components/html/ButtonTag";
import TableTag from "../../components/html/TableTag";
import IconButton from "@/components/IconButton";
import PaginationRow from "@/components/PaginationRow";
import accessMixin from "@/mixin/accessMixin";
import Modal from "../../components/html/Modal";
import SearchCollapse from "@/components/SearchCollapse";
import currencyFormatMixin from "@/mixin/currencyFormatMixin";

export default {
    name: "List",
    components: {
        TableTag,
        ButtonTag,
        RowColumn,
        Row,
        IconButton,
        PaginationRow,
        Modal,
        SearchCollapse,
    },
    mixins: [accessMixin, currencyFormatMixin],
    data() {
        return {
            customerId: 0,
            breadcrumbItems: [
                {name: 'home', url: ''},
                {name: 'customer.title'},
            ],
            search: {
                givenName: '',
                familyName: '',
                companyName: '',
                page: 1
            },
            headers: [
                {name: 'id', hideOnMobile: true},
                {name: 'name'},
                {name: 'company.telephone'},
                {name: 'actions'}
            ],
            bodyProperties: [
                {
                    name: 'id', bgWarning: this.isBgColorWarning, bgDanger: function () {
                    }, hideOnMobile: true
                },
                {
                    name: 'custom', bgWarning: this.isBgColorWarning, bgDanger: function () {
                    },
                },
                {
                    name: 'telephone', bgWarning: this.isBgColorWarning, bgDanger: function () {
                    },
                },
                {
                    name: 'actions', bgWarning: this.isBgColorWarning, bgDanger: function () {
                    },
                }
            ],
            isShowCheckbox: false,
            selectedCustomers: [],
            isCheckedAll: false,
            isSelectedAll: false,
        }
    },
    computed: {
        ...mapGetters(['getCompanyRemaining', 'getCustomer', 'getCustomersTotalCount', 'getDebt', 'getCustomers', "getUserCompany"])
    },
    methods: {
        ...mapActions(['deleteCustomer', 'searchCustomer', 'fetchDebt', 'fetchCustomers', 'fetchCompanyRemaining', 'fetchUser', 'setBreadcrumbItems']),
        showModal() {
            if (this.selectedCustomers.length > 0) {
                this.$bvModal.show('delete-customer-modal')
            }
        },
        showCheckboxes() {
            if (this.isShowCheckbox) {
                this.headers.shift()
                this.bodyProperties.shift()
            } else {
                this.headers.unshift({name: ''})
                this.bodyProperties.unshift({
                    name: 'checkboxes',
                    bgWarning: this.isBgColorWarning,
                    bgDanger: function () {
                    }
                })
            }

            this.isShowCheckbox = !this.isShowCheckbox

            if (this.selectedCustomers.length > 0) {
                this.selectedCustomers = []
                this.isCheckedAll = false
                this.isSelectedAll = false
            }
        },
        getId(custom) {
            let hasCustomId = this.selectedCustomers.some(function (item) {
                return item === custom.id
            })

            if (hasCustomId) {
                let index = this.selectedCustomers.indexOf(custom.id)
                this.selectedCustomers.splice(index, 1)
            } else {
                this.selectedCustomers.push(custom.id)
            }
        },
        async deleteCustomersAction() {
            this.selectedCustomers.forEach(sale => {
                this.modalOk(sale)
            })
        },
        deleteCustomers() {
            this.$root.showOverlay = true

            this.deleteCustomersAction()
                .then(() => {
                    this.selectedCustomers = []
                    this.$bvModal.hide('delete-customer-modal')
                    this.isCheckedAll = false
                })
                .finally(() => {
                    setTimeout(this.updateCustomers, 1000)
                })
        },
        updateCustomers() {
            this.fetchCustomersTotalDebt(this.$route.params.page)
            this.$root.showOverlay = false
        },
        cancelDelete() {
            this.$bvModal.hide('delete-customer-modal')
            this.selectedCustomers = []
            this.isCheckedAll = false
        },
        selectAll() {
            if (this.isSelectedAll) {
                this.selectedCustomers = []
                this.isSelectedAll = false
            } else {
                this.getCustomers.forEach(custom => {
                    this.selectedCustomers.push(custom.id)
                })

                this.isSelectedAll = true
            }
        },
        isBgColorWarning(item) {
            return this.hasDebts(item)
        },
        hasDebts(item) {
            return item.totalDebt !== 0 || item.debt > 0
        },
        getItemName(item) {
            return item.isCompany ? item.customerCompany.name : item.person.givenName + ' ' + item.person.familyName
        },
        getCustomerName(item) {
            return item.customer.isCompany ?
                item.customer.customerCompany.name :
                item.customer.person.givenName + ' ' + item.customer.person.familyName
        },
        deleteOneCustomer(id) {
            this.$root.showOverlay = true

            this.deleteCustomer(id)
                .then(() => {
                    this.searchCustomer(this.search)
                        .then(() => {
                            this.$root.showOverlay = false
                        })
                })
        },
        modalOk(id) {
            this.$root.showOverlay = true

            this.fetchDebt(id)
                .then(() => {
                    if (this.getDebt.customerDebt > 0) {
                        this.customerId = id
                        this.$bvModal.show('delete')
                        this.$root.showOverlay = false
                    } else {
                        this.deleteOneCustomer(id)
                    }
                })
        },
        searchCustomers() {
            this.$root.showOverlay = true
            this.searchCustomer(this.search)
                .catch((err) => {
                    console.log(err)
                })
                .finally(() => {
                    this.$root.showOverlay = false
                });
        },
        resetCustomerSearchInfo() {
            Object.assign(this.$data, this.$options.data.call(this));
            this.searchCustomers()
        }
    },
    created() {
        this.setBreadcrumbItems(this.breadcrumbItems)
    },
    async mounted() {
        if (this.getUserCompany.id === null) {
            await this.fetchUser()
        }
            await this.fetchCompanyRemaining()
    },
}
</script>
<style scoped>
.form-control {
    margin-bottom: 5px !important;
}

.btn-danger {
    background-color: red;
}

.mb-3 {
    margin-bottom: -16px !important;
}

@media (min-width: 576px) {
    .mr-md-1 {
        margin-right: 0.25rem !important;
    }
}
</style>
